import React, { useState, useEffect, useRef } from "react";
import { Title } from "react-head";
import { Stack } from "@mui/material";

import M3Loading from "../Loading/M3Loading";

import Header from "../../../UI/Header/Header";
import HintPopup from "../../../UI/HintDialog/HintDialog";
import PauseOverlay from "../../../UI/PauseOverlay/PauseOverlay";
import PinCluePopup from "../../../shared/PinCluePopup/PinCluePopup";
import MissionsFooter from "../../../shared/MissionsFooter/MissionsFooter";
import CalculatorWrapper from "../Components/Calculator/CalculatorWrapper";
import InstructionDialog from "../../../UI/InstructionDialog/InstructionDialog";
import Mission3HintPopUp from "../../../UI/Mission3HintPopUp/Mission3HintPopUp";
import StickyNotesDrawer from "../../../shared/StickyNotesDrawer/StickyNotesDrawer";

import m3Background from "../../../../assets/images/m3-bg.png";

import useActiveIcon from "../../../../hooks/useActiveIcon";
import usePreloadAssets from "../../../../hooks/usePreloadAssets";

import { getRandomDelayMs } from "../../../../utils/randomDelay";

import "./M3Base.scss";

const delayTime = getRandomDelayMs();

const M3Base = ({
  children,
  clueData,
  hintList,
  showReward,
  Instructions,
  challengeName,
  handlePinClue,
  handleContinue,
  loading = false,
  isShowCalculator = false,
  isShowStickyNote = false,
  handleSaveMission,
  assets = [],
  isShowDownload = false,
  onDownload, // function
  isDownloadComplete = false,
  isReset = false,
  handleResetLevel = () => {},
}) => {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openStickyNotes, setOpenStickyNotes] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);

  const { progress, loaded } = usePreloadAssets(assets);
  const { activeIcon, handleActivateIcon, handleDeactivateIcon } =
    useActiveIcon();

  const toggleStickyNoteDrawer = () => {
    setOpenStickyNotes((prev) => !prev);
    if (openStickyNotes) handleDeactivateIcon();
  };
  const toggleCalculatorDrawer = () => {
    setOpenCalculator((pre) => !pre);
    if (openCalculator) handleDeactivateIcon();
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
    handleSaveMission();
  };

  const handleShowHint = () => {
    handleActivateIcon("hint");
    setShowHint(!showHint);
  };

  useEffect(() => {
    if (isPaused) {
      document.body.setAttribute("class", "paused stage3");
    } else {
      document.body.removeAttribute("class");
    }
  }, [isPaused]);

  useEffect(() => {
    if (isPaused) {
      document.body.setAttribute("data-screen", "paused");
    } else if (showHint) {
      document.body.setAttribute("data-screen", "hint");
    } else if (openCalculator) {
      document.body.setAttribute("data-screen", "calculator");
    } else {
      document.body.removeAttribute("data-screen");
    }
  }, [isPaused, showHint, openCalculator]);

  if (!loaded || loading) {
    return <M3Loading loaded={loaded} progress={progress} />;
  }

  return (
    <>
      <Title>Mission 3 {challengeName}</Title>
      <Stack
        className={`m3-parent`}
        ref={containerRef}
        style={{ backgroundImage: `url(${m3Background})` }}
      >
        <Stack className="dotted-background">
          <Header
            hints={hintList}
            isReset={isReset}
            delayTime={delayTime}
            activeIcon={activeIcon}
            onDownload={onDownload}
            handlePause={handlePause}
            challengeName={challengeName}
            isShowDownload={isShowDownload}
            handleShowHint={handleShowHint}
            stageName={"digitalBreadcrumbs"}
            handleSaveRef={handleSaveMission}
            handleResetLevel={handleResetLevel}
            isShowCalculator={isShowCalculator}
            isShowStickyNote={isShowStickyNote}
            isDownloadComplete={isDownloadComplete}
            handleActivateIcon={handleActivateIcon}
            setShowInstruction={setShowInstruction}
            handleStickyNotes={toggleStickyNoteDrawer}
            handleCalculator={toggleCalculatorDrawer}
          />
          <Stack className="container content">
            {children}
            <StickyNotesDrawer
              open={openStickyNotes}
              toggleNoteDrawer={toggleStickyNoteDrawer}
              containerRef={containerRef}
              noteStorageKey={`m3-${challengeName}`}
            />
            <CalculatorWrapper
              open={openCalculator}
              onClose={toggleCalculatorDrawer}
              level={challengeName}
            />
          </Stack>
          {/* footer */}
          <MissionsFooter />

          {isPaused && (
            <PauseOverlay
              open={isPaused}
              onClose={() => {
                handleDeactivateIcon();
                setIsPaused(false);
              }}
            />
          )}
          {hintList && challengeName !== "Rookie" && (
            <Mission3HintPopUp
              open={showHint}
              onClose={handleShowHint}
              hints={hintList}
              level={challengeName}
            />
          )}
          {hintList && challengeName === "Rookie" && (
            <HintPopup
              hints={hintList}
              onClose={handleShowHint}
              open={showHint}
            />
          )}

          <PinCluePopup
            clueData={clueData}
            onPinClue={handlePinClue}
            onContinue={handleContinue}
            open={showReward}
            IconClass={"clue-Svg"}
          />
          <InstructionDialog
            open={showInstruction}
            onClose={() => setShowInstruction(false)}
            instructions={Instructions}
          />
        </Stack>
      </Stack>
    </>
  );
};

export default React.memo(M3Base);
